import { ARIA_LABEL_MODAL_CLOSE } from '@/shared/constants';
import React, { useEffect, useRef } from 'react';
import HIHButton from './HIHButton';
import layoutStyles from '@/styles/section-layouts.module.scss';
import styles from './Modal.module.scss';

interface ModalProps {
  isOpen: boolean;
  autoFocus?: boolean;
  handleClose: () => void;
  ariaLabel: string;
  closeLabel: string;
  content: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  autoFocus,
  handleClose,
  ariaLabel,
  closeLabel,
  content,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // focus the close button when modal opens
  useEffect(() => {
    if (autoFocus && isOpen) {
      closeButtonRef.current?.focus();
    }
  }, [autoFocus, isOpen]);

  // close on ESC key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      aria-label={ariaLabel}
      aria-modal="true"
      role="dialog"
    >
      <div className={layoutStyles.containerContent}>
        <div className={styles.dialog}>
          {content}

          <HIHButton
            className={styles.closeButton}
            variant="secondary"
            title={closeLabel}
            onClick={handleClose}
            ariaLabel={ARIA_LABEL_MODAL_CLOSE}
            ref={closeButtonRef}
          />
        </div>
      </div>
    </div>
  );
};

export default Modal;
